import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "identifierKey", "isSelectAll", "items", "selectedItems", "onSelect", "onSelectAllToggle"],
  _excluded2 = ["children", "disabled", "label", "paddingInlineStart"],
  _excluded3 = ["canSelect", "children", "disabled", "label", "paddingInlineStart", "value"];
import styled from 'styled-components';
import classNames from 'classnames';
import { GridList } from './GridList';
import { GridListSelectableContext } from './context';
import { LayoutHorizontal } from '../Layout/LayoutHorizontal';
import { useGridListSelectable } from './useGridListSelectable';
import { useGridList } from './useGridList';
import { GridListCheckbox } from './GridListCheckbox';
import { canSelectStyles } from './GridListRowContainer';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function GridListSelectable(_ref) {
  let {
      children,
      identifierKey,
      isSelectAll,
      items,
      selectedItems,
      onSelect,
      onSelectAllToggle
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  return /*#__PURE__*/_jsx(GridListSelectableContext.Provider, {
    value: {
      identifierKey,
      isSelectAll,
      items,
      selectedItems,
      onSelect,
      onSelectAllToggle
    },
    children: /*#__PURE__*/_jsx(GridList, Object.assign({}, rest, {
      children: children
    }))
  });
}
GridListSelectable.Header = GridList.Header;
GridListSelectable.HeaderRow = GridList.HeaderRow;
GridListSelectable.HeaderItem = GridList.HeaderItem;
GridListSelectable.HeaderItemSortable = GridList.HeaderItemSortable;
GridListSelectable.Body = GridList.Body;
const RowSelectAll = _ref2 => {
  let {
      children,
      disabled,
      label = 'Select All',
      paddingInlineStart = 'space-md'
    } = _ref2,
    rest = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  const {
    gapColumn,
    isRefreshing
  } = useGridList();
  const {
    isIndeterminate,
    onSelect,
    onSelectAllToggle,
    items,
    isSelectAll
  } = useGridListSelectable();
  const handleChange = () => {
    if (onSelectAllToggle) {
      onSelectAllToggle(!isSelectAll);
      return;
    }
    if (isSelectAll) {
      onSelect([]);
      return;
    } else {
      onSelect(items);
    }
  };
  return /*#__PURE__*/_jsxs(LayoutHorizontal, {
    gapColumn: gapColumn,
    paddingInlineStart: paddingInlineStart,
    role: "row",
    children: [/*#__PURE__*/_jsx(GridListCheckbox, {
      "aria-label": label,
      checked: isSelectAll,
      disabled: disabled || isRefreshing,
      indeterminate: isIndeterminate,
      isLoading: isRefreshing,
      onChange: handleChange
    }), /*#__PURE__*/_jsx(GridList.HeaderRow, Object.assign({}, rest, {
      flex: "1 1 auto",
      paddingInlineStart: paddingInlineStart,
      children: children
    }))]
  });
};
GridListSelectable.RowSelectAll = RowSelectAll;
const StyledSelectableRow = styled(LayoutHorizontal).withConfig({
  displayName: "GridListSelectable__StyledSelectableRow",
  componentId: "mom9ni-0"
})(["", ""], canSelectStyles);
function Row(_ref3) {
  let {
      canSelect = false,
      children,
      disabled,
      label,
      paddingInlineStart = 'space-md',
      value
    } = _ref3,
    rest = _objectWithoutPropertiesLoose(_ref3, _excluded3);
  const {
    gapColumn,
    isRefreshing
  } = useGridList();
  const {
    identifierKey,
    items,
    selectedItems,
    onSelect
  } = useGridListSelectable();
  const isChecked = !!selectedItems.find(item => {
    if (typeof item === 'object' && identifierKey) {
      return item[identifierKey] === value;
    }
    return item === value;
  });
  const handleChange = () => {
    if (isChecked) {
      onSelect(selectedItems.filter(item => {
        if (typeof item === 'object' && identifierKey) {
          return item[identifierKey] !== value;
        }
        return item !== value;
      }));
    } else {
      onSelect([...selectedItems, items.find(item => {
        if (typeof item === 'object' && identifierKey) {
          return item[identifierKey] === value;
        }
        return item === value;
      })]);
    }
  };
  const ariaLabel = label || value;
  return /*#__PURE__*/_jsxs(StyledSelectableRow, {
    className: classNames(disabled ? 'can-select-disabled' : canSelect && 'can-select'),
    gapColumn: gapColumn,
    paddingInlineStart: paddingInlineStart,
    children: [/*#__PURE__*/_jsx(GridListCheckbox, {
      "aria-label": ariaLabel,
      checked: isChecked,
      disabled: disabled || isRefreshing,
      isLoading: isRefreshing,
      onChange: handleChange
    }), /*#__PURE__*/_jsx(GridList.Row, Object.assign({}, rest, {
      disabled: disabled,
      flex: "1 1 auto",
      paddingInlineStart: paddingInlineStart,
      role: undefined,
      tabIndex: -1,
      children: children
    }))]
  });
}
GridListSelectable.Row = Row;
GridListSelectable.Action = GridList.Action;
GridListSelectable.Item = GridList.Item;
GridListSelectable.ItemAction = GridList.ItemAction;
GridListSelectable.Footer = GridList.Footer;
GridListSelectable.FooterItem = GridList.FooterItem;
GridListSelectable.RefreshProgress = GridList.RefreshProgress;